import { useCallback, useEffect, useRef, useState } from "react";
import { fetchWithAuth, getApiBase } from "../api";
import { useToast } from "../Toast";

export interface BackupStatus {
	state: "idle" | "in-progress" | "success" | "error";
	operation?: "backup" | "restore";
	message?: string;
	timestamp?: string;
}

export interface BackupItem {
	id: string;
	name: string;
}

export const useBackupStatus = (isAuthenticated: boolean) => {
	const [operationStatus, setOperationStatus] = useState<BackupStatus | null>(
		null,
	);
	const [backups, setBackups] = useState<BackupItem[]>([]);
	const [isLoadingBackups, setIsLoadingBackups] = useState(false);
	const { showToast } = useToast();
	const pollingInterval = useRef<ReturnType<typeof setInterval> | null>(null);

	const isPending = operationStatus?.state === "in-progress";
	const isBackingUp = isPending && operationStatus?.operation === "backup";
	const isRestoring = isPending && operationStatus?.operation === "restore";

	const fetchBackups = useCallback(async () => {
		if (!isAuthenticated) return;
		setIsLoadingBackups(true);
		try {
			const response = await fetchWithAuth(`${getApiBase()}/api/v1/backups`);
			if (response.ok) {
				const data = await response.json();
				setBackups(data);
			} else {
				console.error("Failed to fetch backups list");
			}
		} catch (err) {
			console.error("Error fetching backups list:", err);
		} finally {
			setIsLoadingBackups(false);
		}
	}, [isAuthenticated]);

	const checkStatus = useCallback(async () => {
		try {
			const response = await fetchWithAuth(
				`${getApiBase()}/api/v1/backups/status`,
			);
			if (response.ok) {
				const data = await response.json();
				setOperationStatus(data);
			}
		} catch (err) {
			console.error("Failed to fetch backup status:", err);
		}
	}, []);

	useEffect(() => {
		if (isAuthenticated) {
			checkStatus();
			fetchBackups();
		}
	}, [isAuthenticated, checkStatus, fetchBackups]);

	useEffect(() => {
		const pollStatus = async () => {
			try {
				const response = await fetchWithAuth(
					`${getApiBase()}/api/v1/backups/status`,
				);
				if (!response.ok) return;

				const data: BackupStatus = await response.json();
				setOperationStatus(data);

				if (data.state === "success") {
					const op = data.operation || "Operation";
					const capitalizedOp = op.charAt(0).toUpperCase() + op.slice(1);
					showToast(`${capitalizedOp} completed successfully`, "success");
					fetchBackups(); // Refresh backups list
				} else if (data.state === "error") {
					const op = data.operation || "Operation";
					const capitalizedOp = op.charAt(0).toUpperCase() + op.slice(1);
					showToast(`${capitalizedOp} failed: ${data.message}`, "error");
				}
			} catch (err) {
				console.error("Polling error:", err);
			}
		};

		if (isPending) {
			pollingInterval.current = setInterval(pollStatus, 3000);
		} else if (pollingInterval.current) {
			clearInterval(pollingInterval.current);
			pollingInterval.current = null;
		}

		return () => {
			if (pollingInterval.current) {
				clearInterval(pollingInterval.current);
			}
		};
	}, [isPending, showToast, fetchBackups]);

	const triggerBackup = async () => {
		if (isPending) return;

		try {
			const response = await fetchWithAuth(
				`${getApiBase()}/api/v1/backups/trigger`,
				{
					method: "POST",
				},
			);

			if (response.ok) {
				showToast("Backup started in the background", "success");
				setOperationStatus({
					state: "in-progress",
					operation: "backup",
				});
			} else {
				const error = await response.json();
				showToast(error.message || "Failed to start backup", "error");
			}
		} catch (err) {
			console.error("Backup trigger error:", err);
			showToast("Failed to connect to backup service", "error");
		}
	};

	const triggerRestore = async (folderId: string) => {
		if (isPending) return;

		try {
			const response = await fetchWithAuth(
				`${getApiBase()}/api/v1/backups/restore`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
					},
					body: JSON.stringify({ folderId }),
				},
			);

			if (response.ok) {
				showToast("Restore started in the background", "success");
				setOperationStatus({
					state: "in-progress",
					operation: "restore",
				});
			} else {
				const error = await response.json();
				showToast(error.message || "Failed to start restore", "error");
			}
		} catch (err) {
			console.error("Restore trigger error:", err);
			showToast("Failed to connect to backup service", "error");
		}
	};

	return {
		isPending,
		isBackingUp,
		isRestoring,
		operationStatus,
		backups,
		isLoadingBackups,
		fetchBackups,
		triggerBackup,
		triggerRestore,
	};
};
